import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useOrgContext } from '@/hooks/useOrgContext';
import {
    useSurgicalVisitTypes, useSurgeons, surgeonLabel,
    type RoomResource, type VisitType, type SlotOption, type SurgeonOption,
} from './useAscSchedule';

// ── Book case dialog (B1, 2026-08-15) ────────────────────────────────────────
// Patient is SELECTED from cr.patient (identity locks — never free-typed,
// same ruling as Send Consent). Visit type drives duration/buffers via
// find_available_slots; the chosen slot books atomically through
// book_schedule_event (OR reserved as a required participant) and the
// or_cases row is created carrying the schedule_event_id.

interface PatientHit {
    patient_id: number;
    first_name: string | null;
    last_name: string | null;
    birth: string | null;
}

function patientName(p: PatientHit): string {
    return [p.first_name, p.last_name].filter(Boolean).join(' ') || `Patient #${p.patient_id}`;
}
function localHM(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function BookCaseDialog({ dateStr, rooms, busy, findingSlots, error, onClose, onFindSlots, onBook }: {
    dateStr: string;
    rooms: RoomResource[];
    busy: boolean;
    findingSlots: boolean;
    error: string;
    onClose: () => void;
    onFindSlots: (input: { visitTypeId: string; dateStr: string; roomResourceId: string }) => Promise<SlotOption[]>;
    onBook: (input: {
        patient: { patient_id: number; name: string };
        visitType: VisitType; surgeon: SurgeonOption; room: RoomResource;
        slot: SlotOption; idempotencyKey: string;
    }) => Promise<void>;
}) {
    const { org_id: orgId } = useOrgContext();
    const visitTypes = useSurgicalVisitTypes();
    const surgeons = useSurgeons();

    const [query, setQuery] = useState('');
    const [hits, setHits] = useState<PatientHit[]>([]);
    const [patient, setPatient] = useState<PatientHit | null>(null);
    const [visitTypeId, setVisitTypeId] = useState('');
    const [surgeonId, setSurgeonId] = useState<number | ''>('');
    const [roomId, setRoomId] = useState('');
    const [slots, setSlots] = useState<SlotOption[] | null>(null);
    const [slot, setSlot] = useState<SlotOption | null>(null);
    const [localErr, setLocalErr] = useState('');
    const [idempotencyKey] = useState(() => crypto.randomUUID());

    const orRooms = useMemo(() => rooms.filter((r) => r.resource_kind === 'or_suite'), [rooms]);

    useEffect(() => {
        if (!orgId || patient || query.trim().length < 2) { setHits([]); return; }
        const t = setTimeout(async () => {
            const q = query.trim();
            const { data } = await supabase
                .schema('cr').from('patient')
                .select('patient_id, first_name, last_name, birth')
                .eq('org_id', orgId)
                .or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%`)
                .order('last_name').limit(8);
            setHits((data ?? []) as PatientHit[]);
        }, 250);
        return () => clearTimeout(t);
    }, [query, orgId, patient]);

    // Any input change invalidates previously found slots.
    useEffect(() => { setSlots(null); setSlot(null); }, [visitTypeId, roomId, dateStr]);

    const visitType = visitTypes.data?.find((v) => v.appt_type_id === visitTypeId) ?? null;
    const surgeon = surgeons.data?.find((s) => s.provider_id === surgeonId) ?? null;
    const room = orRooms.find((r) => r.resource_id === roomId) ?? null;
    const ready = !!(patient && visitType && surgeon && room);

    async function handleFindSlots() {
        setLocalErr('');
        if (!visitType || !room) { setLocalErr('Pick a procedure and a room first'); return; }
        const found = await onFindSlots({ visitTypeId: visitType.appt_type_id, dateStr, roomResourceId: room.resource_id });
        setSlots(found);
        setSlot(null);
        if (found.length === 0) setLocalErr('No open slots for that room on this day');
    }

    async function handleBook() {
        setLocalErr('');
        if (!ready) { setLocalErr('Complete every field first'); return; }
        if (!slot) { setLocalErr('Pick a time slot'); return; }
        await onBook({
            patient: { patient_id: patient!.patient_id, name: patientName(patient!) },
            visitType: visitType!, surgeon: surgeon!, room: room!, slot, idempotencyKey,
        });
    }

    return (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-6">
            <div className="w-full max-w-lg rounded-lg border border-pt-border bg-pt-panel p-5 max-h-[90vh] overflow-y-auto">
                <h2 className="text-base font-display font-semibold text-pt-text">Book case</h2>
                <p className="text-xs text-pt-text-muted mt-0.5 mb-4">
                    {new Date(`${dateStr}T12:00:00`).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>

                {(error || localErr) && (
                    <div className="rounded border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-3 py-2 mb-3">
                        {localErr || error}
                    </div>
                )}

                <label className="block text-xs text-pt-text-muted mb-1">Patient</label>
                {patient ? (
                    <div className="flex items-center justify-between rounded border border-pt-cyan/40 bg-pt-cyan/5 px-3 py-2 mb-3">
                        <span className="text-sm text-pt-text">
                            {patientName(patient)}
                            {patient.birth ? <span className="text-pt-text-muted"> · DOB {new Date(`${patient.birth}T12:00:00`).toLocaleDateString()}</span> : null}
                        </span>
                        <button type="button" onClick={() => { setPatient(null); setQuery(''); }}
                            className="text-xs text-pt-text-muted hover:text-pt-text">Change</button>
                    </div>
                ) : (
                    <div className="relative mb-3">
                        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search patients by name"
                            className="w-full rounded border border-pt-input-border bg-pt-darker text-pt-text text-sm px-3 py-2" />
                        {hits.length > 0 && (
                            <div className="absolute left-0 right-0 top-full mt-1 z-10 rounded-md border border-pt-border bg-pt-panel py-1 max-h-44 overflow-y-auto">
                                {hits.map((h) => (
                                    <button key={h.patient_id} type="button"
                                        onClick={() => { setPatient(h); setHits([]); }}
                                        className="w-full text-left text-sm text-pt-text px-3 py-1.5 hover:bg-pt-cyan/10">
                                        {patientName(h)}
                                        {h.birth ? <span className="text-xs text-pt-text-muted"> · {new Date(`${h.birth}T12:00:00`).toLocaleDateString()}</span> : null}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className="block text-xs text-pt-text-muted mb-1">Procedure</label>
                        <select value={visitTypeId} onChange={(e) => setVisitTypeId(e.target.value)}
                            className="w-full rounded border border-pt-input-border bg-pt-darker text-pt-text text-sm px-3 py-2">
                            <option value="">Select…</option>
                            {(visitTypes.data ?? []).map((v) => (
                                <option key={v.appt_type_id} value={v.appt_type_id}>
                                    {v.name}{v.duration_mins ? ` · ${v.duration_mins} min` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-pt-text-muted mb-1">Surgeon</label>
                        <select value={surgeonId} onChange={(e) => setSurgeonId(e.target.value ? Number(e.target.value) : '')}
                            className="w-full rounded border border-pt-input-border bg-pt-darker text-pt-text text-sm px-3 py-2">
                            <option value="">Select…</option>
                            {(surgeons.data ?? []).map((s) => (
                                <option key={s.provider_id} value={s.provider_id}>{surgeonLabel(s)}{s.specialty ? ` · ${s.specialty}` : ''}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-end gap-3 mb-3">
                    <div className="flex-1">
                        <label className="block text-xs text-pt-text-muted mb-1">Operating room</label>
                        <select value={roomId} onChange={(e) => setRoomId(e.target.value)}
                            className="w-full rounded border border-pt-input-border bg-pt-darker text-pt-text text-sm px-3 py-2">
                            <option value="">Select…</option>
                            {orRooms.map((r) => <option key={r.resource_id} value={r.resource_id}>{r.display_name}</option>)}
                        </select>
                    </div>
                    <button type="button" disabled={findingSlots || !visitTypeId || !roomId} onClick={handleFindSlots}
                        className="text-sm rounded border border-pt-cyan/60 text-pt-cyan px-3 py-2 hover:bg-pt-cyan/10 disabled:opacity-50 transition-colors">
                        {findingSlots ? 'Finding…' : 'Find open slots'}
                    </button>
                </div>

                {slots && slots.length > 0 && (
                    <div className="mb-4">
                        <label className="block text-xs text-pt-text-muted mb-1.5">Available times</label>
                        <div className="flex flex-wrap gap-1.5">
                            {slots.map((s) => (
                                <button key={s.startsAt} type="button" onClick={() => setSlot(s)}
                                    className={[
                                        'text-xs rounded px-2.5 py-1.5 border transition-colors',
                                        slot?.startsAt === s.startsAt
                                            ? 'border-pt-gold bg-pt-gold/15 text-pt-gold'
                                            : 'border-pt-input-border text-pt-text-muted hover:border-pt-gold/50 hover:text-pt-text',
                                    ].join(' ')}>
                                    {localHM(s.startsAt)}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="text-sm text-pt-text-muted px-3 py-1.5 hover:text-pt-text">Close</button>
                    <button type="button" disabled={busy || !ready || !slot} onClick={handleBook}
                        className="text-sm font-medium rounded bg-pt-gold text-pt-darker px-4 py-1.5 hover:opacity-90 disabled:opacity-50">
                        {busy ? 'Booking…' : 'Book case'}
                    </button>
                </div>
            </div>
        </div>
    );
}
